import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Table } from "shared/app/Table";
import moment from "moment";
import { useMemo } from "react";
import { Label } from "views/rent-reporting/components/SelectPayment";
import { useAuthContext } from "providers/Auth";
import { RentReportingData } from "views/rent-reporting-id/types";
import { Hide } from "shared/components";
import { PaymentStatusElement } from "./components/PaymentStatusElement";

interface RentReportingTableProps {
  reportData?: RentReportingData[];
}

export const RentReportingTable = ({ reportData }: RentReportingTableProps) => {
  const { type } = useAuthContext();

  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "for_month",
        header: "Month",
        cell: (p) => {
          return (
            <span className="text-sm text-content-1000 font-medium">
              {p.getValue() ? moment(p.getValue()).format("MMM YYYY") : ""}
            </span>
          );
        }
      },
      {
        id: "rent_amount fixed_width",
        accessorKey: "rent_amount",
        header: "Rent",
        size: 90,
        cell: (p) => {
          const rentAmount = p.getValue()
            ? `$${p.getValue().cents / 100}`
            : "-";
          return (
            <p className="text-sm text-content-1000 font-medium">
              {rentAmount}
            </p>
          );
        }
      }
    ];
    if (!type.isSDK)
      cols.push({
        id: "status fixed_width",
        accessorKey: "",
        header: "Payment status",
        size: 240,
        cell: (p) => {
          const delinquencyOptions =
            p.row.original?.available_delinquency_options;
          const options = delinquencyOptions?.length
            ? delinquencyOptions.map((option) => {
                return {
                  value: option?.months_delinquency,
                  label: (
                    <Label
                      title={option.name}
                      icon={
                        <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                          <p className="text-danger-500 text-sm font-bold">
                            {option?.months_delinquency}
                          </p>
                        </div>
                      }
                    />
                  )
                };
              })
            : [];

          return PaymentStatusElement({
            status: p.row.original?.status,
            options,
            verificationId: p.row.original.id
          });
        }
      });

    return cols;
  }, []);
  const table = useReactTable({
    data: reportData || [],
    columns: columns as ColumnDef<any, any>[],
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <div className="mt-6">
      <h2 className="text-base text-content-1000 font-medium mb-2">
        Rent reporting
      </h2>
      <Table
        table={table}
        emptyStateTitle="No rent for reporting yet"
        mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
        mobileRow={(row) => {
          const delinquencyOptions =
            row.original?.available_delinquency_options;
          const options = delinquencyOptions?.length
            ? delinquencyOptions.map((option) => {
                return {
                  value: option?.months_delinquency,
                  label: (
                    <Label
                      title={option.name}
                      icon={
                        <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                          <p className="text-danger-500 text-sm font-bold">
                            {option?.months_delinquency}
                          </p>
                        </div>
                      }
                    />
                  )
                };
              })
            : [];

          return (
            <div className="space-y-2">
              <p className="text-sm text-content-700">
                <span>
                  {row.original.for_month
                    ? moment(row.original.for_month).format("MMM YYYY")
                    : ""}
                </span>
                <span> - </span>
                <span>
                  {row.original.rent_amount
                    ? `$${row.original.rent_amount.cents / 100}`
                    : "-"}
                </span>
              </p>
              <Hide if={type.isSDK}>
                <PaymentStatusElement
                  status={row.original?.status}
                  options={options}
                  verificationId={row.original.id}
                />
              </Hide>
            </div>
          );
        }}
      />
    </div>
  );
};
